import { baseApi } from "@/shared/api";
import { decrementPendingActionsCount } from "@/features/auth";
import { authApi } from "@/features/auth/api";
import { pendingActionsApi } from "@/features/self/pending/api/pendingActions";
import type { SignDocumentDetails } from "@/features/self/pending/types/pending.types";
import type {
  CreatePositionAmendmentDto,
  CreateSalaryAmendmentDto,
  CreateOtherAmendmentDto,
  CreateAmendmentResponse,
  AmendmentDetailResponse,
  AmendmentListResponse,
  ApproveAmendmentRequest,
  ApproveAmendmentResponse,
  RejectAmendmentRequest,
  RejectAmendmentResponse,
  RequestRevisionRequest,
  RequestRevisionResponse,
} from "../types";

export const amendmentsApi = baseApi.injectEndpoints({
  endpoints(builder) {
    return {
      getAmendments: builder.query<AmendmentListResponse[], void>({
        query() {
          return {
            url: "/api/hr/amendments/",
            method: "GET",
            credentials: "include",
          };
        },
        providesTags: [{ type: "Amendments" as const, id: "LIST" }],
      }),
      getAmendmentDetail: builder.query<AmendmentDetailResponse, number>({
        query(id) {
          return {
            url: `/api/hr/amendments/${id}/`,
            method: "GET",
            credentials: "include",
          };
        },
        providesTags(_result, _error, id) {
          return [{ type: "Amendments" as const, id }];
        },
      }),
      createPositionAmendment: builder.mutation<CreateAmendmentResponse, CreatePositionAmendmentDto>({
        query(body) {
          return {
            url: "/api/hr/amendments/position/",
            method: "POST",
            body,
            credentials: "include",
          };
        },
        invalidatesTags: [
          { type: "Amendments" as const, id: "LIST" },
          { type: "Contracts" as const, id: "LIST" },
          { type: "SelfService" as const, id: "REQUESTS" },
        ],
      }),
      createSalaryAmendment: builder.mutation<CreateAmendmentResponse, CreateSalaryAmendmentDto>({
        query(body) {
          return {
            url: "/api/hr/amendments/salary/",
            method: "POST",
            body,
            credentials: "include",
          };
        },
        invalidatesTags: [
          { type: "Amendments" as const, id: "LIST" },
          { type: "Contracts" as const, id: "LIST" },
          { type: "SelfService" as const, id: "REQUESTS" },
        ],
      }),
      createOtherAmendment: builder.mutation<CreateAmendmentResponse, CreateOtherAmendmentDto>({
        query(body) {
          return {
            url: "/api/hr/amendments/other/",
            method: "POST",
            body,
            credentials: "include",
          };
        },
        invalidatesTags: [
          { type: "Amendments" as const, id: "LIST" },
          { type: "Contracts" as const, id: "LIST" },
          { type: "SelfService" as const, id: "REQUESTS" },
        ],
      }),
      approveAmendment: builder.mutation<ApproveAmendmentResponse, ApproveAmendmentRequest>({
        query({ id, cms_signature }) {
          return {
            url: `/api/hr/amendments/${id}/approve/`,
            method: "POST",
            body: { cms_signature },
            credentials: "include",
          };
        },
        invalidatesTags: (_result, _error, { id }) => [
          { type: "Amendments" as const, id },
          { type: "Amendments" as const, id: "LIST" },
          { type: "Contracts" as const, id: "LIST" },
          { type: "SelfService" as const, id: "REQUESTS" },
          "PendingActions",
        ],
        async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
          const pendingListPatch = dispatch(
            pendingActionsApi.util.updateQueryData("getPendingActions", undefined, (draft) => {
              const idx = draft.items.findIndex(
                (item) =>
                  (item.action_required === "approve_draft" && "clause_section" in item.details) ||
                  (item.action_required === "sign_document" &&
                    "document_id" in item.details &&
                    (item.details as SignDocumentDetails).source_type === "amendment_application"),
              );
              if (idx !== -1) {
                draft.items.splice(idx, 1);
                draft.summary.total_count = Math.max(0, draft.summary.total_count - 1);
              }
            }),
          );
          const pendingCountPatch = dispatch(
            authApi.util.updateQueryData("getPendingCount", undefined, (draft) => {
              if (draft.count > 0) draft.count -= 1;
            }),
          );
          try {
            await queryFulfilled;
            dispatch(decrementPendingActionsCount());
            dispatch(
              amendmentsApi.util.updateQueryData("getAmendmentDetail", id, (draft) => {
                draft.status = "signing_pending";
              }),
            );
          } catch {
            pendingListPatch.undo();
            pendingCountPatch.undo();
          }
        },
      }),
      rejectAmendment: builder.mutation<RejectAmendmentResponse, { id: number; data: RejectAmendmentRequest }>({
        query({ id, data }) {
          return {
            url: `/api/hr/amendments/${id}/reject/`,
            method: "POST",
            body: data,
            credentials: "include",
          };
        },
        invalidatesTags: (_result, _error, { id }) => [
          { type: "Amendments" as const, id },
          { type: "Amendments" as const, id: "LIST" },
          { type: "Contracts" as const, id: "LIST" },
          { type: "SelfService" as const, id: "REQUESTS" },
          "PendingActions",
        ],
        async onQueryStarted(_, { dispatch, queryFulfilled }) {
          const pendingListPatch = dispatch(
            pendingActionsApi.util.updateQueryData("getPendingActions", undefined, (draft) => {
              const idx = draft.items.findIndex(
                (item) => item.action_required === "approve_draft" && "clause_section" in item.details,
              );
              if (idx !== -1) {
                draft.items.splice(idx, 1);
                draft.summary.total_count = Math.max(0, draft.summary.total_count - 1);
              }
            }),
          );
          const pendingCountPatch = dispatch(
            authApi.util.updateQueryData("getPendingCount", undefined, (draft) => {
              if (draft.count > 0) draft.count -= 1;
            }),
          );
          try {
            await queryFulfilled;
            dispatch(decrementPendingActionsCount());
          } catch {
            pendingListPatch.undo();
            pendingCountPatch.undo();
          }
        },
      }),
      requestRevision: builder.mutation<RequestRevisionResponse, { id: number; data: RequestRevisionRequest }>({
        query({ id, data }) {
          return {
            url: `/api/hr/amendments/${id}/request-revision/`,
            method: "POST",
            body: data,
            credentials: "include",
          };
        },
        invalidatesTags: (_result, _error, { id }) => [
          { type: "Amendments" as const, id },
          { type: "Amendments" as const, id: "LIST" },
          { type: "SelfService" as const, id: "REQUESTS" },
          "PendingActions",
        ],
        async onQueryStarted(_, { dispatch, queryFulfilled }) {
          const pendingListPatch = dispatch(
            pendingActionsApi.util.updateQueryData("getPendingActions", undefined, (draft) => {
              const idx = draft.items.findIndex(
                (item) => item.action_required === "approve_draft" && "clause_section" in item.details,
              );
              if (idx !== -1) {
                draft.items.splice(idx, 1);
                draft.summary.total_count = Math.max(0, draft.summary.total_count - 1);
              }
            }),
          );
          const pendingCountPatch = dispatch(
            authApi.util.updateQueryData("getPendingCount", undefined, (draft) => {
              if (draft.count > 0) draft.count -= 1;
            }),
          );
          try {
            await queryFulfilled;
            dispatch(decrementPendingActionsCount());
          } catch {
            pendingListPatch.undo();
            pendingCountPatch.undo();
          }
        },
      }),
      revokeAmendment: builder.mutation<{ signing_document_id: string }, number>({
        query(id) {
          return {
            url: `/api/hr/amendments/${id}/revoke/`,
            method: "POST",
            body: {},
            credentials: "include",
          };
        },
        invalidatesTags(_result, _error, id) {
          return [
            { type: "Amendments" as const, id },
            { type: "Amendments" as const, id: "LIST" },
            { type: "SelfService" as const, id: "REQUESTS" },
            "PendingActions",
          ];
        },
      }),
    };
  },
});

export const {
  useGetAmendmentsQuery,
  useGetAmendmentDetailQuery,
  useCreatePositionAmendmentMutation,
  useCreateSalaryAmendmentMutation,
  useCreateOtherAmendmentMutation,
  useApproveAmendmentMutation,
  useRejectAmendmentMutation,
  useRequestRevisionMutation,
  useRevokeAmendmentMutation,
} = amendmentsApi;
